export const DYNAMIC_WHATS_FLOW_NODE_TYPE = "whatsapp_flow_dynamic_options";
export const DYNAMIC_WHATS_FLOW_OUTPUTS = [
  { id: "sent", label: "Sent" },
  { id: "send_failed", label: "Send failed" },
];

// XploreByte application safety bounds; these are not Meta protocol limits.
export const DYNAMIC_WHATS_FLOW_LIMITS = Object.freeze({
  dependencies: 20,
  rowsPerDependency: 200,
  optionId: 128,
  optionTitle: 30,
  configBytes: 128 * 1024,
});

export const createDynamicWhatsFlowConfig = () => ({
  schemaVersion: 1,
  dataChannelMode: "DataExchange",
  flowDefinitionId: "",
  flowRevisionId: "",
  entry: { bodyText: "", buttonText: "Open", startScreenId: "" },
  timeoutMinutes: 1440,
  dependencies: [],
});

const identifier = value => /^[A-Za-z0-9_.-]+$/.test(String(value || ""));

export function validateDynamicWhatsFlowConfig(config) {
  if (config?.schemaVersion !== 1 || config?.dataChannelMode !== "DataExchange")
    return { isValid: false, message: "Dynamic Whats Form configuration version is invalid." };
  if (!config?.flowDefinitionId || !config?.flowRevisionId)
    return { isValid: false, message: "Select an eligible Data Exchange Form revision." };
  if (!config?.entry?.buttonText?.trim() || config.entry.buttonText.trim().length > 20)
    return { isValid: false, message: "Button text is required and must be 20 characters or fewer." };
  if (!identifier(config?.entry?.startScreenId))
    return { isValid: false, message: "Select a valid start screen." };
  const dependencies = Array.isArray(config?.dependencies) ? config.dependencies : [];
  if (!dependencies.length || dependencies.length > DYNAMIC_WHATS_FLOW_LIMITS.dependencies)
    return { isValid: false, message: `Configure 1-${DYNAMIC_WHATS_FLOW_LIMITS.dependencies} dependencies.` };

  const ids = new Set();
  const targets = new Set();
  const graph = new Map();
  for (const dependency of dependencies) {
    const required = ["id", "screenId", "parentComponent", "parentDataKey", "targetComponent", "targetDataKey"];
    if (required.some(key => !identifier(dependency?.[key])))
      return { isValid: false, message: "Every dependency needs valid stable screen, parent, target, and data-key identities." };
    if (ids.has(dependency.id)) return { isValid: false, message: "Dependency IDs must be unique." };
    ids.add(dependency.id);
    const target = `${dependency.screenId}:${dependency.targetComponent}`;
    if (targets.has(target)) return { isValid: false, message: "A target component can be configured only once." };
    targets.add(target);
    graph.set(dependency.parentComponent, dependency.targetComponent);
    if (dependency?.source?.kind !== "ConfiguredLookup")
      return { isValid: false, message: "ConfiguredLookup is the only supported V1 source." };
    const rows = Array.isArray(dependency?.source?.rows) ? dependency.source.rows : [];
    if (!rows.length || rows.length > DYNAMIC_WHATS_FLOW_LIMITS.rowsPerDependency)
      return { isValid: false, message: `Each dependency needs 1-${DYNAMIC_WHATS_FLOW_LIMITS.rowsPerDependency} lookup rows.` };
    const childKeys = new Set();
    for (const row of rows) {
      if (!identifier(row?.parentId) || String(row.parentId).length > DYNAMIC_WHATS_FLOW_LIMITS.optionId
        || !identifier(row?.childId) || String(row.childId).length > DYNAMIC_WHATS_FLOW_LIMITS.optionId
        || !String(row?.title || "").trim() || String(row.title).trim().length > DYNAMIC_WHATS_FLOW_LIMITS.optionTitle)
        return { isValid: false, message: "Lookup rows require bounded stable parent/child IDs and display titles." };
      const key = `${row.parentId}:${row.childId}`;
      if (childKeys.has(key)) return { isValid: false, message: "Child IDs must be unique for each parent result." };
      childKeys.add(key);
    }
  }
  for (const start of graph.keys()) {
    const seen = new Set();
    let current = start;
    while (graph.has(current)) {
      if (seen.has(current)) return { isValid: false, message: "Dynamic field dependencies cannot contain cycles." };
      seen.add(current);
      current = graph.get(current);
    }
  }
  if (new Blob([JSON.stringify(config)]).size > DYNAMIC_WHATS_FLOW_LIMITS.configBytes)
    return { isValid: false, message: "Dynamic Whats Form configuration exceeds the XploreByte size bound." };
  return { isValid: true, message: "" };
}
